import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Mail, Phone, User, Calendar, Target, Download, Search,
  Sparkles, Battery, Scale, Moon, Smile, Activity, Filter, TrendingUp, Users,
  Beaker, Building2, Briefcase, ArrowRight, CheckCircle, Trophy, Eye, MousePointer
} from "lucide-react";
import { useState, useMemo } from "react";
import { startOfDay, startOfWeek, startOfMonth } from "date-fns";

// Peptide stack config for display names
const PEPTIDE_STACKS = [
  { id: 'metabolic', title: 'Metabolic Reset + Repair', color: 'bg-red-500' },
  { id: 'springbreak', title: 'Spring Break Stack', color: 'bg-pink-500' },
  { id: 'summer', title: 'Summer Maintenance', color: 'bg-yellow-500' },
  { id: 'flu', title: 'Flu Defense Stack', color: 'bg-green-500' },
  { id: 'gut', title: 'Gut Restoration', color: 'bg-teal-500' },
  { id: 'surgery', title: 'Pre/Post Surgery', color: 'bg-blue-500' },
  { id: 'athletic', title: 'Workout / Athletic Stack', color: 'bg-orange-500' },
  { id: 'stress', title: 'Stress & Burnout Stack', color: 'bg-purple-500' },
  { id: 'longevity', title: 'Longevity Stack', color: 'bg-indigo-500' },
];

const WELLNESS_GOALS_CONFIG = [
  { id: "energy", label: "Energy", icon: Battery, color: "bg-warm" },
  { id: "weight", label: "Weight", icon: Scale, color: "bg-green-500" },
  { id: "skin", label: "Skin", icon: Sparkles, color: "bg-pink-500" },
  { id: "hair", label: "Hair", icon: Sparkles, color: "bg-purple-500" },
  { id: "sleep", label: "Sleep", icon: Moon, color: "bg-indigo-500" },
  { id: "mood", label: "Mood", icon: Smile, color: "bg-primary" },
  { id: "recovery", label: "Recovery", icon: Activity, color: "bg-red-500" },
];

interface ConsolidatedLead {
  id: string;
  source: 'membership' | 'assessment' | 'consultation' | 'employer';
  name: string;
  email: string;
  phone: string | null;
  interests: string[];
  challenge: string | null;
  peptideInterest: boolean | null;
  location: string | null;
  status: string;
  createdAt: string;
  additionalNotes: string | null;
  orderlyClicked?: boolean;
  nudaClicked?: boolean;
  companyName?: string;
  companySize?: string;
}

export function LeadsWithInterests() {
  const [searchQuery, setSearchQuery] = useState("");
  const [interestFilter, setInterestFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [partnerFilter, setPartnerFilter] = useState<string>("all");

  const today = startOfDay(new Date());
  const weekStart = startOfWeek(today);
  const monthStart = startOfMonth(today);

  // Fetch consultation leads with Orderly/Nuda tracking
  const { data: consultationLeads = [] } = useQuery({
    queryKey: ['leads-consultation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consultation_leads')
        .select(`
          id, name, email, phone, message, created_at, status, notes,
          location_id, orderly_clicked, nuda_clicked,
          locations:location_id (name)
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch memberships with wellness data
  const { data: memberships = [] } = useQuery({
    queryKey: ['leads-interests-memberships'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('memberships')
        .select(`
          id,
          customer_name,
          customer_email,
          customer_phone,
          wellness_goals,
          wellness_challenge,
          interested_in_peptides,
          pipeline_stage,
          created_at,
          locations:location_id (name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch NudaVitae assessments
  const { data: assessments = [] } = useQuery({
    queryKey: ['leads-interests-assessments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nudavitae_assessments')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch CTA clicks (for Aesthetics and Nav tracking)
  const { data: ctaClicks = [] } = useQuery({
    queryKey: ['cta-clicks-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cta_clicks')
        .select('id, cta_type, cta_location, created_at, source_url')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch peptide stack clicks
  const { data: stackClicks = [] } = useQuery({
    queryKey: ['peptide-stack-clicks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cta_clicks')
        .select('id, cta_type, cta_location, created_at, source_url')
        .eq('cta_type', 'peptide_stack')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all page visits (any cta_type that contains page info)
  const { data: allClicks = [] } = useQuery({
    queryKey: ['all-cta-clicks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cta_clicks')
        .select('id, cta_type, cta_location, created_at, source_url')
        .order('created_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data || [];
    },
  });

  // Filter by date helper
  const filterByDate = <T extends { created_at?: string | null; createdAt?: string }>(items: T[], startDate: Date) => {
    return items.filter(item => {
      const date = item.created_at || item.createdAt;
      return date && new Date(date) >= startDate;
    });
  };

  // Partner-specific metrics from consultation leads
  const orderlyLeads = consultationLeads.filter((l: any) => l.orderly_clicked);
  const nudaLeads = consultationLeads.filter((l: any) => l.nuda_clicked);
  const bothPartnersLeads = consultationLeads.filter((l: any) => l.orderly_clicked && l.nuda_clicked);

  // Aesthetics clicks (nav clicks to mytruetox.com)
  const navClicks = ctaClicks.filter((c: any) => c.cta_type === 'nav_click');
  const aestheticsClicks = navClicks.filter((c: any) => c.cta_location === 'aesthetics');
  const employerPageClicks = navClicks.filter((c: any) => c.cta_location === 'employers');

  // Stack click analytics - group by stack ID
  const stackLeaderboard = useMemo(() => {
    const stackCounts: Record<string, { views: number; books: number; total: number }> = {};
    
    // Initialize all stacks
    PEPTIDE_STACKS.forEach(stack => {
      stackCounts[stack.id] = { views: 0, books: 0, total: 0 };
    });
    
    // Count clicks by stack
    stackClicks.forEach((click: any) => {
      const location = click.cta_location || '';
      const parts = location.split('_');
      const stackId = parts[0];
      const action = parts[1];
      
      if (stackCounts[stackId]) {
        if (action === 'view') {
          stackCounts[stackId].views++;
        } else if (action === 'book') {
          stackCounts[stackId].books++;
        }
        stackCounts[stackId].total++;
      }
    });
    
    // Sort by total clicks descending
    return PEPTIDE_STACKS
      .map(stack => ({
        ...stack,
        ...stackCounts[stack.id],
      }))
      .sort((a, b) => b.total - a.total);
  }, [stackClicks]);

  // Page visit tracking from source URLs
  const pageVisits = useMemo(() => {
    const pageCounts: Record<string, number> = {};
    
    allClicks.forEach((click: any) => {
      const url = click.source_url || '/';
      // Normalize URL
      const path = url.startsWith('/') ? url : new URL(url, 'https://truewellness.lovable.app').pathname;
      pageCounts[path] = (pageCounts[path] || 0) + 1;
    });
    
    return Object.entries(pageCounts)
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [allClicks]);

  // Total stack clicks
  const totalStackClicks = stackClicks.length;
  const stackClicksToday = filterByDate(stackClicks, today).length;
  const stackClicksWeek = filterByDate(stackClicks, weekStart).length;
  const stackClicksMonth = filterByDate(stackClicks, monthStart).length;

  // Time-based partner metrics
  const orderlyToday = filterByDate(orderlyLeads, today).length;
  const orderlyWeek = filterByDate(orderlyLeads, weekStart).length;
  const orderlyMonth = filterByDate(orderlyLeads, monthStart).length;

  const nudaToday = filterByDate(nudaLeads, today).length;
  const nudaWeek = filterByDate(nudaLeads, weekStart).length;
  const nudaMonth = filterByDate(nudaLeads, monthStart).length;

  const assessmentsToday = filterByDate(assessments, today).length;
  const assessmentsWeek = filterByDate(assessments, weekStart).length;
  const assessmentsMonth = filterByDate(assessments, monthStart).length;

  // Aesthetics time-based metrics
  const aestheticsToday = filterByDate(aestheticsClicks, today).length;
  const aestheticsWeek = filterByDate(aestheticsClicks, weekStart).length;
  const aestheticsMonth = filterByDate(aestheticsClicks, monthStart).length;

  // Employer time-based metrics
  const employerToday = filterByDate(employerPageClicks, today).length;
  const employerWeek = filterByDate(employerPageClicks, weekStart).length;
  const employerMonth = filterByDate(employerPageClicks, monthStart).length;

  // Consolidate all leads
  const consolidatedLeads = useMemo((): ConsolidatedLead[] => {
    const leads: ConsolidatedLead[] = [];

    // Add consultation leads
    consultationLeads.forEach((c: any) => {
      leads.push({
        id: c.id,
        source: 'consultation',
        name: c.name,
        email: c.email,
        phone: c.phone,
        interests: [],
        challenge: c.message,
        peptideInterest: c.nuda_clicked,
        location: c.locations?.name || null,
        status: c.status || 'new',
        createdAt: c.created_at,
        additionalNotes: c.notes,
        orderlyClicked: c.orderly_clicked,
        nudaClicked: c.nuda_clicked,
      });
    });

    // Add memberships
    memberships.forEach((m: any) => {
      leads.push({
        id: m.id,
        source: 'membership',
        name: m.customer_name,
        email: m.customer_email,
        phone: m.customer_phone,
        interests: m.wellness_goals || [],
        challenge: m.wellness_challenge,
        peptideInterest: m.interested_in_peptides,
        location: m.locations?.name || null,
        status: m.pipeline_stage || 'lead',
        createdAt: m.created_at,
        additionalNotes: null,
      });
    });

    // Add assessments
    assessments.forEach((a: any) => {
      leads.push({
        id: a.id,
        source: 'assessment',
        name: `${a.first_name} ${a.last_name}`,
        email: a.email,
        phone: a.phone,
        interests: a.primary_goals || [],
        challenge: a.additional_notes,
        peptideInterest: true,
        location: null,
        status: a.status,
        createdAt: a.created_at,
        additionalNotes: a.admin_notes,
        nudaClicked: true,
      });
    });

    return leads.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [consultationLeads, memberships, assessments]);

  // Apply filters
  const filteredLeads = useMemo(() => {
    return consolidatedLeads.filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!lead.name.toLowerCase().includes(query) && 
            !lead.email.toLowerCase().includes(query) &&
            !(lead.phone?.toLowerCase().includes(query))) {
          return false;
        }
      }

      if (interestFilter !== 'all' && !lead.interests.includes(interestFilter)) {
        return false;
      }

      if (sourceFilter !== 'all' && lead.source !== sourceFilter) {
        return false;
      }

      if (partnerFilter === 'orderly' && !lead.orderlyClicked) {
        return false;
      }
      if (partnerFilter === 'nuda' && !lead.nudaClicked) {
        return false;
      }
      if (partnerFilter === 'assessment' && lead.source !== 'assessment') {
        return false;
      }

      return true;
    });
  }, [consolidatedLeads, searchQuery, interestFilter, sourceFilter, partnerFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: consolidatedLeads.length,
    consultations: consolidatedLeads.filter(l => l.source === 'consultation').length,
    memberships: consolidatedLeads.filter(l => l.source === 'membership').length,
    assessments: consolidatedLeads.filter(l => l.source === 'assessment').length,
    orderlyClicks: orderlyLeads.length,
    nudaClicks: nudaLeads.length,
    aestheticsClicks: aestheticsClicks.length,
    employerClicks: employerPageClicks.length,
  }), [consolidatedLeads, orderlyLeads, nudaLeads, aestheticsClicks, employerPageClicks]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getGoalLabel = (goalId: string) => {
    const goal = WELLNESS_GOALS_CONFIG.find(g => g.id === goalId);
    return goal?.label || goalId;
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Source', 'Orderly Clicked', 'Nuda Clicked', 'Interests', 'Location', 'Status', 'Created At'];
    const rows = filteredLeads.map(lead => [
      lead.name,
      lead.email,
      lead.phone || '',
      lead.source,
      lead.orderlyClicked ? 'Yes' : 'No',
      lead.nudaClicked ? 'Yes' : 'No',
      lead.interests.map(getGoalLabel).join('; '),
      lead.location || '',
      lead.status,
      formatDate(lead.createdAt),
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partner-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 via-purple-600 to-blue-600 p-6 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-1">Partner & Funnel Activity</h1>
            <p className="text-white/80">Track Orderly, Nuda Labs, Aesthetics & Employer engagement</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5 text-sm">
              <Scale className="h-4 w-4" />
              <span className="font-medium">Orderly</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5 text-sm">
              <Beaker className="h-4 w-4" />
              <span className="font-medium">Nuda</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5 text-sm">
              <Sparkles className="h-4 w-4" />
              <span className="font-medium">Aesthetics</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5 text-sm">
              <Building2 className="h-4 w-4" />
              <span className="font-medium">Employers</span>
            </div>
          </div>
        </div>
      </div>

      {/* Partner KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card className="border-orange-200 dark:border-orange-800 bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-950/50 dark:to-orange-900/30">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-orange-700 dark:text-orange-300">OrderlyMeds Clicks</CardTitle>
            <Scale className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.orderlyClicks}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-orange-600/70">
              <span>Today: {orderlyToday}</span>
              <span>•</span>
              <span>Week: {orderlyWeek}</span>
              <span>•</span>
              <span>Month: {orderlyMonth}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/50 dark:to-purple-900/30">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300">Nuda Labs Clicks</CardTitle>
            <Beaker className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.nudaClicks}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-purple-600/70">
              <span>Today: {nudaToday}</span>
              <span>•</span>
              <span>Week: {nudaWeek}</span>
              <span>•</span>
              <span>Month: {nudaMonth}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-violet-200 dark:border-violet-800 bg-gradient-to-br from-violet-50 to-violet-100/50 dark:from-violet-950/50 dark:to-violet-900/30">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-violet-700 dark:text-violet-300">NudaVitae Assessments</CardTitle>
            <Sparkles className="h-5 w-5 text-violet-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-violet-600 dark:text-violet-400">{stats.assessments}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-violet-600/70">
              <span>Today: {assessmentsToday}</span>
              <span>•</span>
              <span>Week: {assessmentsWeek}</span>
              <span>•</span>
              <span>Month: {assessmentsMonth}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/50 dark:to-blue-900/30">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Leads</CardTitle>
            <Users className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.total}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-blue-600/70">
              <span>Consults: {stats.consultations}</span>
              <span>•</span>
              <span>Members: {stats.memberships}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-pink-200 dark:border-pink-800 bg-gradient-to-br from-pink-50 to-pink-100/50 dark:from-pink-950/50 dark:to-pink-900/30">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-pink-700 dark:text-pink-300">Aesthetics Clicks</CardTitle>
            <Sparkles className="h-5 w-5 text-pink-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-pink-600 dark:text-pink-400">{stats.aestheticsClicks}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-pink-600/70">
              <span>Today: {aestheticsToday}</span>
              <span>•</span>
              <span>Week: {aestheticsWeek}</span>
              <span>•</span>
              <span>Month: {aestheticsMonth}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-800/30">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-300">Employer Clicks</CardTitle>
            <Building2 className="h-5 w-5 text-slate-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-600 dark:text-slate-400">{stats.employerClicks}</div>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-600/70">
              <span>Today: {employerToday}</span>
              <span>•</span>
              <span>Week: {employerWeek}</span>
              <span>•</span>
              <span>Month: {employerMonth}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peptide Stack Leaderboard & Page Visits */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Stack Leaderboard */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Peptide Stack Leaderboard
                </CardTitle>
                <CardDescription>Product clicks ranked high to low</CardDescription>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-foreground">{totalStackClicks}</div>
                <div className="text-xs text-muted-foreground">Total Clicks</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {stackLeaderboard.map((stack, index) => {
                const maxClicks = stackLeaderboard[0]?.total || 1;
                const percentage = (stack.total / maxClicks) * 100;
                
                return (
                  <div key={stack.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          index === 0 ? 'bg-amber-500' : 
                          index === 1 ? 'bg-slate-400' : 
                          index === 2 ? 'bg-amber-700' : 'bg-muted-foreground'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="font-medium">{stack.title}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Eye className="h-3 w-3" />
                          <span>{stack.views}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MousePointer className="h-3 w-3" />
                          <span>{stack.books}</span>
                        </div>
                        <span className="font-bold w-8 text-right">{stack.total}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${stack.color}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> = Learn More</span>
                <span className="flex items-center gap-1"><MousePointer className="h-3 w-3" /> = Book Now</span>
              </div>
              <div>
                Today: {stackClicksToday} • Week: {stackClicksWeek} • Month: {stackClicksMonth}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Page Visits */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              Top Page Visits
            </CardTitle>
            <CardDescription>Most visited pages by click activity</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {pageVisits.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No page visit data yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageVisits.map((page, index) => (
                    <TableRow key={page.path}>
                      <TableCell className="font-bold text-muted-foreground">{index + 1}</TableCell>
                      <TableCell className="font-mono text-sm">{page.path || '/'}</TableCell>
                      <TableCell className="text-right font-bold">{page.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="orderly" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="orderly" className="gap-1.5 text-xs sm:text-sm">
            <Scale className="h-4 w-4" />
            <span className="hidden sm:inline">Orderly</span>
            <Badge variant="secondary" className="ml-1 hidden md:inline">{orderlyLeads.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="nuda" className="gap-1.5 text-xs sm:text-sm">
            <Beaker className="h-4 w-4" />
            <span className="hidden sm:inline">Nuda</span>
            <Badge variant="secondary" className="ml-1 hidden md:inline">{nudaLeads.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="aesthetics" className="gap-1.5 text-xs sm:text-sm">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Aesthetics</span>
            <Badge variant="secondary" className="ml-1 hidden md:inline">{aestheticsClicks.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="employers" className="gap-1.5 text-xs sm:text-sm">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Employers</span>
            <Badge variant="secondary" className="ml-1 hidden md:inline">{employerPageClicks.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1.5 text-xs sm:text-sm">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">All</span>
            <Badge variant="secondary" className="ml-1 hidden md:inline">{stats.total}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* OrderlyMeds Leads Tab */}
        <TabsContent value="orderly">
          <Card>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="h-5 w-5 text-orange-500" />
                    OrderlyMeds GLP-1 Leads
                  </CardTitle>
                  <CardDescription>Leads who clicked through to OrderlyMeds for weight loss</CardDescription>
                </div>
                <Button onClick={exportToCSV} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {orderlyLeads.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No OrderlyMeds leads yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Also Nuda?</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderlyLeads.slice(0, 20).map((lead: any) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm hover:text-primary">
                              <Mail className="w-3 h-3" />{lead.email}
                            </a>
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-sm hover:text-primary">
                                <Phone className="w-3 h-3" />{lead.phone}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{lead.locations?.name || '—'}</TableCell>
                        <TableCell>
                          {lead.nuda_clicked ? (
                            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                              <CheckCircle className="w-3 h-3 mr-1" />Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(lead.created_at)}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`mailto:${lead.email}?subject=Your%20GLP-1%20Weight%20Loss%20Journey`)}
                          >
                            <Mail className="w-4 h-4 mr-1" />Contact
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nuda Labs Leads Tab */}
        <TabsContent value="nuda">
          <Card>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Beaker className="h-5 w-5 text-purple-500" />
                    Nuda Labs Peptide Leads
                  </CardTitle>
                  <CardDescription>Leads interested in personalized peptide protocols</CardDescription>
                </div>
                <Button onClick={exportToCSV} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {nudaLeads.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No Nuda Labs leads yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Also Orderly?</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nudaLeads.slice(0, 20).map((lead: any) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm hover:text-primary">
                              <Mail className="w-3 h-3" />{lead.email}
                            </a>
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-sm hover:text-primary">
                                <Phone className="w-3 h-3" />{lead.phone}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{lead.locations?.name || '—'}</TableCell>
                        <TableCell>
                          {lead.orderly_clicked ? (
                            <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                              <CheckCircle className="w-3 h-3 mr-1" />Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(lead.created_at)}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`mailto:${lead.email}?subject=Your%20Personalized%20Peptide%20Protocol`)}
                          >
                            <Mail className="w-4 h-4 mr-1" />Contact
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aesthetics Clicks Tab */}
        <TabsContent value="aesthetics">
          <Card>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-pink-500" />
                    Aesthetics Navigation Clicks
                  </CardTitle>
                  <CardDescription>Visitors who clicked the Aesthetics link to mytruetox.com</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {aestheticsClicks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No Aesthetics clicks tracked yet</p>
                  <p className="text-sm mt-1">Clicks will appear once visitors use the header navigation</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source Page</TableHead>
                      <TableHead>Date & Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aestheticsClicks.slice(0, 30).map((click: any) => (
                      <TableRow key={click.id}>
                        <TableCell className="font-medium">{click.source_url || '/'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(click.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employers Navigation Clicks Tab */}
        <TabsContent value="employers">
          <Card>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-slate-600" />
                    Employer Page Interest
                  </CardTitle>
                  <CardDescription>Visitors who clicked the Employers link in navigation</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {employerPageClicks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No Employer page clicks tracked yet</p>
                  <p className="text-sm mt-1">B2B interest will appear once visitors use the header navigation</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source Page</TableHead>
                      <TableHead>Date & Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employerPageClicks.slice(0, 30).map((click: any) => (
                      <TableRow key={click.id}>
                        <TableCell className="font-medium">{click.source_url || '/'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(click.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Leads Tab */}
        <TabsContent value="all">
          <Card>
            <CardHeader className="border-b">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle>All Consumer Leads</CardTitle>
                  <CardDescription>Consolidated view of all lead sources</CardDescription>
                </div>
                <Button onClick={exportToCSV} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-full md:w-40">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="membership">Membership</SelectItem>
                    <SelectItem value="assessment">Assessment</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                  <SelectTrigger className="w-full md:w-44">
                    <SelectValue placeholder="Partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Partners</SelectItem>
                    <SelectItem value="orderly">OrderlyMeds</SelectItem>
                    <SelectItem value="nuda">Nuda Labs</SelectItem>
                    <SelectItem value="assessment">Assessment Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-3">
                <Badge variant="secondary">{filteredLeads.length} leads</Badge>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Orderly</TableHead>
                    <TableHead>Nuda</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No leads found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeads.slice(0, 50).map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">{lead.email}</div>
                          {lead.phone && <div className="text-xs text-muted-foreground">{lead.phone}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{lead.source}</Badge>
                        </TableCell>
                        <TableCell>
                          {lead.orderlyClicked ? (
                            <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.nudaClicked || lead.source === 'assessment' ? (
                            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{lead.location || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(lead.createdAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}